/**
 * Render report datasets to PDF, CSV, Excel, JSON
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { formatCurrency, formatDate } from '../../lib/regions/au';
import type { ReportDataset, ReportFormat } from '../../types/report.types';
import {
  FINANCIAL_SUMMARY_MOM_DELTA_HEADERS,
  financialSummaryBudgetPerformanceTableStyles,
  financialSummaryBudgetTableStyles,
  financialSummaryCategoryTableStyles,
  financialSummaryPeriodComparisonTableStyles,
  financialSummaryPrimarySectionTableStyles,
  financialSummaryBusinessExpenseDetailTableStyles,
  financialSummaryDataNoticeTableStyles,
  financialSummaryReportSummaryTableStyles,
  financialSummaryTakeawaysTableStyles,
  FINANCIAL_SUMMARY_PDF_SEMANTIC,
  formatFinancialSummaryReportGeneratedDate,
  REPORT_PDF_FOOTER_RESERVE_MM,
  withBusinessPdfFooterMargin,
} from './financial-summary-pdf-styles';
import { BUSINESS_EXPENSE_GST_DISCLAIMER } from './business-expense-gst-summary';
import {
  normaliseBusinessMerchantName,
  sanitiseBusinessCategoryDisplayName,
} from './business-expense-merchant';
import { getReportDefinition } from '../../config/report-definitions';
import {
  formatFinancialSummaryDateRangeLabel,
  formatFinancialSummaryPdfTitle,
} from './financial-summary-dataset';
import type { BudgetPerformanceStatus } from '../../types/report.types';

export interface RenderOptions {
  includeCharts: boolean;
  includeRawData: boolean;
}

function formatAud(amount: number): string {
  return formatCurrency(amount);
}

function formatChangeAud(amount: number | undefined): string {
  if (amount == null) return '—';
  const prefix = amount > 0 ? '+' : '';
  return `${prefix}${formatAud(amount)}`;
}

function formatReportPdfDate(isoDate: string): string {
  const parsed = parseISO(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return format(parsed, 'd MMM yyyy');
}

function businessExpenseDescriptionCell(rawDescription: string): string {
  const label = normaliseBusinessMerchantName(rawDescription);
  if (label === rawDescription.trim()) {
    return label;
  }
  return `${label}\n${rawDescription.trim()}`;
}

function isFinancialSummary(dataset: ReportDataset): boolean {
  return dataset.reportType === 'monthly_financial_summary';
}

function isBusinessExpenseReport(dataset: ReportDataset): boolean {
  return dataset.reportType === 'business_expense';
}

function usesStyledReportPdf(dataset: ReportDataset): boolean {
  return isFinancialSummary(dataset) || isBusinessExpenseReport(dataset);
}

const SUMMARY_FILL_COLOR: [number, number, number] = [239, 246, 255];
const SUMMARY_HEAD_FILL: [number, number, number] = [219, 234, 254];

function appendGenerationSummaryCsvLines(lines: string[], dataset: ReportDataset): void {
  const summary = dataset.generationCriteria?.summary;
  if (!summary) return;
  lines.push('"Report Summary",""');
  lines.push(`"Date Range","${summary.dateRangeLabel.replace(/"/g, '""')}"`);
  lines.push(`"Categories","${summary.categoriesLabel.replace(/"/g, '""')}"`);
  lines.push(`"Accounts","${summary.accountsLabel.replace(/"/g, '""')}"`);
  if (
    dataset.reportingMetadata?.countryName &&
    !isFinancialSummary(dataset) &&
    !isBusinessExpenseReport(dataset)
  ) {
    lines.push(
      `"Country of residence","${dataset.reportingMetadata.countryName.replace(/"/g, '""')}"`
    );
  }
  lines.push('');
}

function generationSummarySheetRows(dataset: ReportDataset): unknown[][] {
  const summary = dataset.generationCriteria?.summary;
  if (!summary) {
    return [
      ['Report', dataset.title],
      ['Period', dataset.period.label],
      ['From', formatDate(dataset.period.startDate)],
      ['To', formatDate(dataset.period.endDate)],
    ];
  }
  const rows: unknown[][] = [['Report Summary', '']];
  rows.push(['Date Range', summary.dateRangeLabel]);
  rows.push(['Categories', summary.categoriesLabel]);
  rows.push(['Accounts', summary.accountsLabel]);
  if (dataset.reportingMetadata?.countryName) {
    rows.push(['Country of residence', dataset.reportingMetadata.countryName]);
  }
  rows.push(['From', formatDate(dataset.period.startDate)]);
  rows.push(['To', formatDate(dataset.period.endDate)]);
  return rows;
}

function appendPdfReportSummaryTable(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const summary = dataset.generationCriteria?.summary;
  if (!summary) {
    return startY;
  }

  const dateRangeDisplay = usesStyledReportPdf(dataset)
    ? formatFinancialSummaryDateRangeLabel(dataset.period)
    : summary.dateRangeLabel;
  const body: string[][] = [
    ['Date Range', dateRangeDisplay],
    ['Categories', summary.categoriesLabel],
    ['Accounts', summary.accountsLabel],
  ];
  if (
    dataset.reportingMetadata?.countryName &&
    !isFinancialSummary(dataset) &&
    !isBusinessExpenseReport(dataset)
  ) {
    body.push(['Country of residence', dataset.reportingMetadata.countryName]);
  }

  if (usesStyledReportPdf(dataset)) {
    body.push(['Report generated', formatFinancialSummaryReportGeneratedDate()]);
    autoTable(doc, {
      startY,
      head: [[{ content: 'Report Summary', colSpan: 2, styles: { halign: 'left' } }]],
      body,
      ...financialSummaryReportSummaryTableStyles(),
    });
  } else {
    autoTable(doc, {
      startY,
      head: [['Report Summary', '']],
      body,
      theme: 'plain',
      headStyles: {
        fillColor: SUMMARY_HEAD_FILL,
        textColor: [17, 24, 39],
        fontStyle: 'bold',
        halign: 'left',
      },
      styles: {
        fillColor: SUMMARY_FILL_COLOR,
        textColor: [55, 65, 81],
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 70, fontStyle: 'normal' },
        1: { halign: 'right', fontStyle: 'bold' },
      },
    });
  }

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function budgetStatusPdfColor(status: BudgetPerformanceStatus): [number, number, number] {
  if (status === 'Over') return FINANCIAL_SUMMARY_PDF_SEMANTIC.negative;
  if (status === 'Near limit') return FINANCIAL_SUMMARY_PDF_SEMANTIC.warning;
  return FINANCIAL_SUMMARY_PDF_SEMANTIC.positive;
}

function styledReportPdfTitle(dataset: ReportDataset): string {
  if (isFinancialSummary(dataset)) return formatFinancialSummaryPdfTitle(dataset.period);
  if (isBusinessExpenseReport(dataset)) {
    return getReportDefinition('business_expense')?.title ?? 'Business Expense Report';
  }
  return dataset.title;
}

function appendStyledReportPdfTitle(doc: jsPDF, dataset: ReportDataset, startY: number): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(26, 26, 26);
  doc.text(styledReportPdfTitle(dataset), 14, startY);
  let y = startY + 10;
  if (isBusinessExpenseReport(dataset)) {
    const identity = dataset.businessExpenseInsights;
    const parts: string[] = [];
    if (identity?.businessName?.trim()) parts.push(identity.businessName.trim());
    if (identity?.businessAbn?.trim()) parts.push(`ABN ${identity.businessAbn.trim()}`);
    if (parts.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(parts.join(' · '), 14, y + 4);
      doc.setTextColor(0, 0, 0);
      y += 8;
    }
  }
  doc.setTextColor(0, 0, 0);
  return y + 4;
}

function appendFinancialSummaryKeyTakeaways(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const items = dataset.financialSummaryInsights?.keyTakeaways ?? [];
  if (!items.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Key takeaways']],
    body: items.map((item, index) => [`${index + 1}. ${item.message}`]),
    ...financialSummaryTakeawaysTableStyles(),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendFinancialSummaryDataNotices(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const notices = dataset.financialSummaryInsights?.dataNotices ?? [];
  if (!notices.length) return startY;
  const tableWidth = doc.internal.pageSize.getWidth() - 28;
  autoTable(doc, {
    startY,
    tableWidth,
    head: [['Data notices']],
    body: notices.map((n) => [n.message]),
    ...financialSummaryDataNoticeTableStyles(),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function formatBusinessUsePercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(0)}%`;
}

function formatReceiptOnFile(attached: boolean | undefined): string {
  return attached ? 'Yes' : 'No';
}

function formatBusinessAccountLabel(accountName: string | null | undefined): string {
  if (!accountName?.trim()) return '—';
  return accountName.replace(/\s+/g, ' ').replace(/^Credit Card\s+/i, '').trim();
}

function startBusinessExpenseAppendixPage(doc: jsPDF): number {
  doc.addPage();
  return 14;
}

function appendBusinessExpenseDataNotices(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const notices = dataset.businessExpenseInsights?.dataNotices ?? [];
  if (!notices.length) return startY;
  const tableWidth = doc.internal.pageSize.getWidth() - 28;
  autoTable(doc, {
    startY,
    tableWidth,
    head: [['Data notices']],
    body: notices.map((n) => [n.message]),
    ...withBusinessPdfFooterMargin(financialSummaryDataNoticeTableStyles()),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseSummaryKpis(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const kpis = dataset.businessExpenseInsights?.summaryKpis;
  if (!kpis) return startY;
  const body: string[][] = [
    ['Total business spend', String(kpis.transactionCount), formatAud(kpis.totalSpendAud)],
  ];
  body.push([
    'Claimable amount (where business % set)',
    '—',
    kpis.claimableAmountAud != null ? formatAud(kpis.claimableAmountAud) : '—',
  ]);
  if (kpis.unapportionedSpendAud > 0) {
    body.push([
      'Spend without business-use %',
      '—',
      formatAud(kpis.unapportionedSpendAud),
    ]);
  }
  if (dataset.businessExpenseInsights?.showGstSection) {
    body.push([
      'GST included (where confirmed)',
      '—',
      formatAud(kpis.gstIncludedConfirmedAud ?? 0),
    ]);
  }
  autoTable(doc, {
    startY,
    head: [['Business expense summary', 'Transactions', 'Amount (AUD)']],
    body,
    ...withBusinessPdfFooterMargin(financialSummaryPrimarySectionTableStyles()),
    rowPageBreak: 'avoid',
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' },
      2: { halign: 'right' },
    },
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseVendorTable(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const vendors = dataset.businessExpenseInsights?.vendors ?? [];
  if (!vendors.length) return startY;
  autoTable(doc, {
    startY,
    head: [
      [
        'By vendor',
        'Charges',
        'Months',
        'Total (AUD)',
        'Business %',
        'Claimable (AUD)',
        'Receipts',
      ],
    ],
    body: vendors.map((v) => [
      v.vendorLabel,
      String(v.chargeCount),
      String(v.monthsActive),
      formatAud(v.totalAmountAud),
      formatBusinessUsePercent(v.businessUsePercent),
      v.claimableAmountAud != null ? formatAud(v.claimableAmountAud) : '—',
      v.receiptStatus,
    ]),
    ...withBusinessPdfFooterMargin(financialSummaryCategoryTableStyles()),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseQuarterlyTable(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const quarters = dataset.businessExpenseInsights?.quarterlyTotals ?? [];
  if (!quarters.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Quarter (AU FY)', 'Transactions', 'Total (AUD)', 'Claimable (AUD)']],
    body: quarters.map((q) => [
      q.quarterLabel,
      String(q.transactionCount),
      formatAud(q.totalAmountAud),
      q.claimableAmountAud != null ? formatAud(q.claimableAmountAud) : '—',
    ]),
    ...withBusinessPdfFooterMargin(financialSummaryPrimarySectionTableStyles()),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseYoyTable(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const rows = dataset.businessExpenseInsights?.categoryYearOnYear ?? [];
  if (!rows.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Year-on-year by category', 'This period', 'Prior year', 'Change']],
    body: rows.map((r) => [
      r.categoryName,
      formatAud(r.currentAmountAud),
      formatAud(r.priorAmountAud),
      r.changePercent != null
        ? `${r.changeAud >= 0 ? '+' : ''}${formatAud(r.changeAud)} (${r.changePercent.toFixed(1)}%)`
        : formatChangeAud(r.changeAud),
    ]),
    ...withBusinessPdfFooterMargin(financialSummaryPrimarySectionTableStyles()),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseInformationalFindings(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const findings =
    dataset.businessExpenseInsights?.informationalFindings.filter((f) => f.severity === 'low') ??
    [];
  if (!findings.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Informational context']],
    body: findings.slice(0, 12).map((f) => [f.message]),
    ...withBusinessPdfFooterMargin(financialSummaryTakeawaysTableStyles()),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendFinancialSummaryNextSteps(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const steps = dataset.financialSummaryInsights?.nextSteps ?? [];
  if (!steps.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Recommended next steps']],
    body: steps.map((step, index) => [`${index + 1}. ${step.action}`]),
    ...financialSummaryTakeawaysTableStyles(),
  });
  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
}

function appendBusinessExpenseTransactionsByPrimaryCategory(
  doc: jsPDF,
  dataset: ReportDataset,
  startY: number
): number {
  const groups =
    dataset.businessExpenseDetailByPrimaryCategory ??
    (dataset.rawTransactions?.length
      ? [
          {
            primaryCategoryName: 'All business expenses',
            amount: dataset.businessExpenseTotal ?? 0,
            transactionCount: dataset.rawTransactions.length,
            percent: 100,
            transactions: [...dataset.rawTransactions].sort((a, b) => a.date.localeCompare(b.date)),
          },
        ]
      : []);

  let y = startY;
  const pageHeight = doc.internal.pageSize.getHeight();

  if (groups.length) {
    y = startBusinessExpenseAppendixPage(doc);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Appendix: transaction detail', 14, y);
    doc.setFont('helvetica', 'normal');
    y += 8;
  }

  for (const group of groups) {
    if (y > pageHeight - REPORT_PDF_FOOTER_RESERVE_MM - 40) {
      doc.addPage();
      y = 14;
    }

    const sectionTitle = sanitiseBusinessCategoryDisplayName(group.primaryCategoryName);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(
      `${sectionTitle} — ${group.transactionCount} transactions · ${group.percent.toFixed(1)}% · ${formatAud(group.amount)}`,
      14,
      y
    );
    doc.setFont('helvetica', 'normal');
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Description', 'Account', 'Bus. %', 'Rcpt', 'Amount (AUD)']],
      body: group.transactions.map((tx, index) => [
        String(index + 1),
        formatReportPdfDate(tx.date),
        businessExpenseDescriptionCell(tx.description),
        formatBusinessAccountLabel(tx.accountName),
        formatBusinessUsePercent(tx.businessUsePercent),
        formatReceiptOnFile(tx.receiptAttached),
        formatAud(tx.expenseAmountAud ?? Math.abs(tx.amount)),
      ]),
      ...financialSummaryBusinessExpenseDetailTableStyles(),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  return y;
}

export function renderReportToCsv(dataset: ReportDataset, options: RenderOptions): string {
  const lines: string[] = [`"${dataset.title}"`, ''];
  appendGenerationSummaryCsvLines(lines, dataset);

  if (dataset.emptyMessage) {
    lines.push(`"Note","${dataset.emptyMessage}"`);
  }

  if (dataset.incomeVsExpenses) {
    lines.push('"Income vs Expenses"');
    lines.push('"Total Income",' + formatAud(dataset.incomeVsExpenses.totalIncome));
    lines.push('"Total Expenses",' + formatAud(dataset.incomeVsExpenses.totalExpenses));
    lines.push('"Net Savings",' + formatAud(dataset.incomeVsExpenses.netPosition));
    if (dataset.incomeVsExpenses.savingsRate != null) {
      lines.push('"Savings Rate",' + `${dataset.incomeVsExpenses.savingsRate.toFixed(1)}%`);
    }
    lines.push('');
  }

  if (dataset.periodComparison) {
    const pc = dataset.periodComparison;
    lines.push('"Period Comparison"');
    lines.push(`"Prior period","${pc.priorPeriodLabel.replace(/"/g, '""')}"`);
    lines.push('"Prior income",' + formatAud(pc.priorTotalIncome));
    lines.push('"Prior expenses",' + formatAud(pc.priorTotalExpenses));
    lines.push('"Prior net savings",' + formatAud(pc.priorNetPosition));
    lines.push('"Income change",' + formatChangeAud(pc.incomeChange));
    lines.push('"Expense change",' + formatChangeAud(pc.expenseChange));
    lines.push('"Net savings change",' + formatChangeAud(pc.netChange));
    lines.push('');
  }

  if (dataset.incomeSummary) {
    lines.push('"Income Summary"');
    lines.push('"PAYG / Salary",' + formatAud(dataset.incomeSummary.paygIncome));
    lines.push('"Interest",' + formatAud(dataset.incomeSummary.interestIncome));
    lines.push('"Dividends",' + formatAud(dataset.incomeSummary.dividendIncome));
    lines.push('"Other",' + formatAud(dataset.incomeSummary.otherIncome));
    lines.push('"Total",' + formatAud(dataset.incomeSummary.totalIncome));
    lines.push('');
  }

  if (dataset.expenseByCategory?.length) {
    if (isFinancialSummary(dataset) || isBusinessExpenseReport(dataset)) {
      lines.push('"Category","Transactions","Percent","Amount"');
      for (const row of dataset.expenseByCategory) {
        lines.push(
          `"${row.categoryName}",${row.transactionCount ?? 0},${row.percent.toFixed(1)}%,${formatAud(row.amount)}`
        );
      }
    } else {
      lines.push('"Category","Amount","Percent"');
      for (const row of dataset.expenseByCategory) {
        lines.push(`"${row.categoryName}",${formatAud(row.amount)},${row.percent.toFixed(1)}%`);
      }
    }
    lines.push('');
  }

  if (dataset.budgetPerformance?.length) {
    if (isFinancialSummary(dataset)) {
      lines.push('"Budget Category","Budget limit","Avg spent per month","Total spent","Remaining"');
      for (const row of dataset.budgetPerformance) {
        lines.push(
          `"${row.categoryName}","${formatAud(row.budget)} ${row.budgetPeriodLabel}",${formatAud(row.averageSpentPerMonth)},${formatAud(row.totalSpent)},${formatAud(row.remaining)}`
        );
      }
    } else {
      lines.push('"Budget Category","Budget","Total spent","Remaining"');
      for (const row of dataset.budgetPerformance) {
        lines.push(
          `"${row.categoryName}",${formatAud(row.budget)},${formatAud(row.totalSpent)},${formatAud(row.remaining)}`
        );
      }
    }
    lines.push('');
  }

  if (dataset.monthlyTrend?.length) {
    if (isFinancialSummary(dataset)) {
      lines.push('"Month","Income","Expenses","Net","Income change","Expense change","Net change"');
      for (const row of dataset.monthlyTrend) {
        lines.push(
          `"${row.monthLabel}",${formatAud(row.income)},${formatAud(row.expenses)},${formatAud(row.net)},${formatChangeAud(row.incomeChange)},${formatChangeAud(row.expenseChange)},${formatChangeAud(row.netChange)}`
        );
      }
    } else {
      lines.push('"Month","Income","Expenses","Net"');
      for (const row of dataset.monthlyTrend) {
        lines.push(
          `"${row.monthLabel}",${formatAud(row.income)},${formatAud(row.expenses)},${formatAud(row.net)}`
        );
      }
    }
    lines.push('');
  }

  if (dataset.deductionThemes?.length) {
    lines.push('"Deduction Theme","Amount","Transactions"');
    for (const theme of dataset.deductionThemes) {
      lines.push(`"${theme.theme}",${formatAud(theme.totalAmount)},${theme.transactionCount}`);
    }
    lines.push('');
  }

  if (dataset.gstSummary) {
    if (isBusinessExpenseReport(dataset)) {
      lines.push('"GST — business expenses (informational)"');
      lines.push(
        '"GST included (where confirmed)",' +
          formatAud(dataset.gstSummary.gstIncludedConfirmed ?? 0)
      );
      lines.push(
        '"GST status not verified",' +
          formatAud(
            dataset.gstSummary.gstUnverifiedSpend ??
              dataset.gstSummary.inclusiveTotal + dataset.gstSummary.exclusiveTotal
          )
      );
    } else {
      lines.push('"GST Summary (informational)"');
      lines.push('"GST-inclusive spend",' + formatAud(dataset.gstSummary.inclusiveTotal));
      lines.push('"GST-exclusive spend",' + formatAud(dataset.gstSummary.exclusiveTotal));
      lines.push('"Net GST component",' + formatAud(dataset.gstSummary.netGst));
    }
    lines.push('');
  }

  if (isBusinessExpenseReport(dataset) && dataset.businessExpenseInsights?.vendors.length) {
    lines.push('"By vendor","Charges","Months active","Total","Business %","Claimable","Receipt status"');
    for (const v of dataset.businessExpenseInsights.vendors) {
      lines.push(
        `"${v.vendorLabel}",${v.chargeCount},${v.monthsActive},${formatAud(v.totalAmountAud)},${v.businessUsePercent ?? ''},${v.claimableAmountAud != null ? formatAud(v.claimableAmountAud) : ''},"${v.receiptStatus}"`
      );
    }
    lines.push('');
  }

  if (isBusinessExpenseReport(dataset) && dataset.businessExpenseInsights?.quarterlyTotals.length) {
    lines.push('"Quarter","Transactions","Total","Claimable"');
    for (const q of dataset.businessExpenseInsights.quarterlyTotals) {
      lines.push(
        `"${q.quarterLabel}",${q.transactionCount},${formatAud(q.totalAmountAud)},${q.claimableAmountAud != null ? formatAud(q.claimableAmountAud) : ''}`
      );
    }
    lines.push('');
  }

  if (options.includeRawData && dataset.rawTransactions?.length) {
    if (isBusinessExpenseReport(dataset)) {
      lines.push(
        '"Date","Vendor","Description","Account","Business %","Receipt","Category","Amount (AUD)","Claimable (AUD)"'
      );
      for (const tx of dataset.rawTransactions) {
        lines.push(
          `"${formatReportPdfDate(tx.date)}","${(tx.vendorLabel ?? '').replace(/"/g, '""')}","${tx.description.replace(/"/g, '""')}","${(tx.accountName ?? '').replace(/"/g, '""')}",${tx.businessUsePercent ?? ''},${tx.receiptAttached ? 'Yes' : 'No'},"${(tx.categoryName ?? '').replace(/"/g, '""')}",${formatAud(tx.expenseAmountAud ?? Math.abs(tx.amount))},${tx.claimableAmountAud != null ? formatAud(tx.claimableAmountAud) : ''}`
        );
      }
      lines.push('');
    } else {
      lines.push('"Date","Description","Category","Amount"');
      for (const tx of dataset.rawTransactions) {
        lines.push(
          `"${formatDate(tx.date)}","${tx.description.replace(/"/g, '""')}","${tx.categoryName ?? ''}",${formatAud(tx.amount)}`
        );
      }
    }
  }

  return lines.join('\n');
}

export function renderReportToJson(dataset: ReportDataset, options: RenderOptions): string {
  const payload = {
    ...dataset,
    rawTransactions: options.includeRawData ? dataset.rawTransactions : undefined,
    charts: options.includeCharts
      ? [{ type: 'category_breakdown', data: dataset.expenseByCategory ?? [] }]
      : undefined,
  };
  return JSON.stringify(payload, null, 2);
}

export function renderReportToExcel(dataset: ReportDataset, options: RenderOptions): ArrayBuffer {
  const wb = XLSX.utils.book_new();
  const summaryRows = generationSummarySheetRows(dataset);
  if (dataset.emptyMessage) summaryRows.push(['Note', dataset.emptyMessage]);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

  if (dataset.incomeVsExpenses) {
    const incomeRows: unknown[][] = [
      ['Metric', 'Amount'],
      ['Total Income', dataset.incomeVsExpenses.totalIncome],
      ['Total Expenses', dataset.incomeVsExpenses.totalExpenses],
      ['Net Savings', dataset.incomeVsExpenses.netPosition],
    ];
    if (dataset.incomeVsExpenses.savingsRate != null) {
      incomeRows.push(['Savings Rate (%)', dataset.incomeVsExpenses.savingsRate]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(incomeRows), 'Income vs Expenses');
  }

  if (dataset.periodComparison) {
    const pc = dataset.periodComparison;
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['Metric', 'Current', 'Prior period', 'Change'],
        ['Income', pc.currentTotalIncome, pc.priorTotalIncome, pc.incomeChange],
        ['Expenses', pc.currentTotalExpenses, pc.priorTotalExpenses, pc.expenseChange],
        ['Net savings', pc.currentNetPosition, pc.priorNetPosition, pc.netChange],
        ['Prior period label', pc.priorPeriodLabel, '', ''],
      ]),
      'Period Comparison'
    );
  }

  if (dataset.expenseByCategory?.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        dataset.expenseByCategory.map((r) =>
          isFinancialSummary(dataset)
            ? {
                Category: r.categoryName,
                Amount: r.amount,
                Transactions: r.transactionCount ?? 0,
                Percent: r.percent,
              }
            : {
                Category: r.categoryName,
                Amount: r.amount,
                Percent: r.percent,
              }
        )
      ),
      isFinancialSummary(dataset) ? 'Category Spending' : 'Categories'
    );
  }

  if (dataset.budgetPerformance?.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        dataset.budgetPerformance.map((r) =>
          isFinancialSummary(dataset)
            ? {
                Category: r.categoryName,
                'Budget limit': r.budget,
                'Budget period': r.budgetPeriodLabel,
                'Avg spent per month': r.averageSpentPerMonth,
                'Total spent': r.totalSpent,
                Remaining: r.remaining,
              }
            : {
                Category: r.categoryName,
                Budget: r.budget,
                'Total spent': r.totalSpent,
                Remaining: r.remaining,
              }
        )
      ),
      'Budget'
    );
  }

  if (dataset.monthlyTrend?.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        dataset.monthlyTrend.map((r) =>
          isFinancialSummary(dataset)
            ? {
                Month: r.monthLabel,
                Income: r.income,
                Expenses: r.expenses,
                Net: r.net,
                'Income change': r.incomeChange ?? null,
                'Expense change': r.expenseChange ?? null,
                'Net change': r.netChange ?? null,
              }
            : {
                Month: r.monthLabel,
                Income: r.income,
                Expenses: r.expenses,
                Net: r.net,
              }
        )
      ),
      'Month-over-Month'
    );
  }

  if (options.includeCharts && dataset.expenseByCategory?.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(dataset.expenseByCategory),
      'Charts'
    );
  }

  if (options.includeRawData && dataset.rawTransactions?.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        dataset.rawTransactions.map((tx) => ({
          Date: formatDate(tx.date),
          Description: tx.description,
          Category: tx.categoryName,
          Amount: tx.amount,
        }))
      ),
      'Transactions'
    );
  }

  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

function appendPdfFooter(doc: jsPDF, dataset: ReportDataset): void {
  if (!dataset.reportingMetadata) return;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();
  const meta = dataset.reportingMetadata;
  const business = isBusinessExpenseReport(dataset);

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(100);
    if (business) {
      doc.text(BUSINESS_EXPENSE_GST_DISCLAIMER, 14, pageHeight - 26, { maxWidth: 180 });
      doc.text(meta.disclaimer, 14, pageHeight - 12, { maxWidth: 180 });
    } else {
      doc.text(`Jurisdiction: ${meta.countryName} (${meta.countryCode})`, 14, pageHeight - 22);
      doc.text(meta.disclaimer, 14, pageHeight - 16, { maxWidth: 180 });
    }
    doc.setTextColor(0);
  }
}

function composeReportPdfDocument(dataset: ReportDataset, options: RenderOptions): jsPDF {
  const doc = new jsPDF();

  let y = usesStyledReportPdf(dataset) ? appendStyledReportPdfTitle(doc, dataset, 18) : 20;
  y = appendPdfReportSummaryTable(doc, dataset, y);

  if (isFinancialSummary(dataset)) {
    y = appendFinancialSummaryKeyTakeaways(doc, dataset, y);
    y = appendFinancialSummaryDataNotices(doc, dataset, y);
  }

  if (isBusinessExpenseReport(dataset) && dataset.businessExpenseInsights) {
    y = appendBusinessExpenseSummaryKpis(doc, dataset, y);
  }

  if (dataset.emptyMessage) {
    doc.text(dataset.emptyMessage, 14, y);
    y += 10;
  }

  if (dataset.incomeVsExpenses || dataset.reportType === 'monthly_financial_summary') {
    const iv = dataset.incomeVsExpenses ?? {
      totalIncome: 0,
      totalExpenses: 0,
      netPosition: 0,
      savingsRate: null,
    };
    const ivBody: string[][] = [
      ['Total income', formatAud(iv.totalIncome)],
    ];
    if (isFinancialSummary(dataset) && iv.regularIncome != null && iv.oneOffIncome != null) {
      ivBody.push(['Regular income', formatAud(iv.regularIncome)]);
      ivBody.push(['One-off income', formatAud(iv.oneOffIncome)]);
    }
    ivBody.push(['Total expenses', formatAud(iv.totalExpenses)]);
    ivBody.push(['Net savings', formatAud(iv.netPosition)]);
    if (iv.savingsRate != null) {
      ivBody.push(['Savings rate', `${iv.savingsRate.toFixed(1)}%`]);
    }
    if (isFinancialSummary(dataset) && iv.underlyingSavingsRate != null) {
      ivBody.push(['Underlying savings rate', `${iv.underlyingSavingsRate.toFixed(1)}%`]);
    }
    autoTable(doc, {
      startY: y,
      head: [['Income vs expenses', 'Amount (AUD)']],
      body: ivBody,
      ...(isFinancialSummary(dataset)
        ? {
            ...financialSummaryPrimarySectionTableStyles(),
            didParseCell: (data) => {
              if (data.section !== 'body' || data.column.index !== 1) return;
              const row = data.row.raw as string[];
              const label = String(row[0] ?? '');
              if (label === 'Net savings' || label === 'Underlying savings rate') {
                const raw = dataset.incomeVsExpenses?.netPosition ?? 0;
                const rate =
                  label === 'Underlying savings rate'
                    ? dataset.incomeVsExpenses?.underlyingSavingsRate
                    : dataset.incomeVsExpenses?.savingsRate;
                const value = label === 'Net savings' ? raw : (rate ?? 0);
                if (value < 0) data.cell.styles.textColor = FINANCIAL_SUMMARY_PDF_SEMANTIC.negative;
                else if (value > 0)
                  data.cell.styles.textColor = FINANCIAL_SUMMARY_PDF_SEMANTIC.positive;
              }
            },
          }
        : {}),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (dataset.periodComparison && isFinancialSummary(dataset)) {
    const pc = dataset.periodComparison;
    if (pc.comparisonReliable === false) {
      autoTable(doc, {
        startY: y,
        head: [['Period comparison']],
        body: [
          [
            'Not enough data to compare — prior period data is too sparse for this range.',
          ],
        ],
        ...financialSummaryPeriodComparisonTableStyles(),
        columnStyles: { 0: { cellWidth: 'wrap', halign: 'left' } },
      });
    } else {
      autoTable(doc, {
        startY: y,
        head: [['Period comparison', 'Current period', 'Prior period', 'Change']],
        body: [
          [
            'Income',
            formatAud(pc.currentTotalIncome),
            formatAud(pc.priorTotalIncome),
            formatChangeAud(pc.incomeChange),
          ],
          [
            'Expenses',
            formatAud(pc.currentTotalExpenses),
            formatAud(pc.priorTotalExpenses),
            formatChangeAud(pc.expenseChange),
          ],
          [
            'Net savings',
            formatAud(pc.currentNetPosition),
            formatAud(pc.priorNetPosition),
            formatChangeAud(pc.netChange),
          ],
          ['Prior period', pc.priorPeriodLabel, '—', '—'],
        ],
        ...financialSummaryPeriodComparisonTableStyles(),
      });
    }
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (
    dataset.reportType === 'profit_and_loss' ||
    dataset.revenueTotal != null ||
    dataset.expenseTotal != null ||
    dataset.netProfit != null
  ) {
    autoTable(doc, {
      startY: y,
      head: [['Profit & Loss', 'Amount (AUD)']],
      body: [
        ['Total revenue', formatAud(dataset.revenueTotal ?? 0)],
        ['Total operating expenses', formatAud(dataset.expenseTotal ?? 0)],
        ['Net profit', formatAud(dataset.netProfit ?? 0)],
      ],
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (
    dataset.reportType === 'business_expense' &&
    dataset.businessExpenseTotal != null &&
    !dataset.businessExpenseInsights
  ) {
    const businessTxCount =
      dataset.businessExpenseTransactionCount ??
      dataset.generationCriteria?.transactionCountTotal ??
      dataset.rawTransactions?.length ??
      0;
    autoTable(doc, {
      startY: y,
      head: [['Business expense summary', 'Transactions', 'Amount (AUD)']],
      body: [
        [
          'Total business expenses',
          String(businessTxCount),
          formatAud(dataset.businessExpenseTotal),
        ],
      ],
      ...financialSummaryPrimarySectionTableStyles(),
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (dataset.incomeSummary && !isFinancialSummary(dataset)) {
    autoTable(doc, {
      startY: y,
      head: [['Income Type', 'Amount (AUD)']],
      body: [
        ['PAYG / Salary', formatAud(dataset.incomeSummary.paygIncome)],
        ['Interest', formatAud(dataset.incomeSummary.interestIncome)],
        ['Dividends', formatAud(dataset.incomeSummary.dividendIncome)],
        ['Other', formatAud(dataset.incomeSummary.otherIncome)],
        ['Total', formatAud(dataset.incomeSummary.totalIncome)],
      ],
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  const showCategoryTable =
    dataset.reportType === 'monthly_financial_summary' ||
    dataset.reportType === 'category_spending_analysis' ||
    dataset.reportType === 'profit_and_loss' ||
    (dataset.expenseByCategory?.length && dataset.reportType !== 'business_expense');

  if (showCategoryTable || (dataset.reportType === 'business_expense' && dataset.expenseByCategory?.length)) {
    const categoryHead =
      dataset.reportType === 'business_expense'
        ? 'Business expenses by category'
        : isFinancialSummary(dataset)
          ? 'Category spending breakdown'
          : 'Category Spending';
    const categoryColumns =
      isBusinessExpenseReport(dataset) || isFinancialSummary(dataset)
        ? [categoryHead, 'Transactions', '%', 'Amount (AUD)']
        : [categoryHead, 'Amount', '%'];
    autoTable(doc, {
      startY: y,
      head: [categoryColumns],
      body: dataset.expenseByCategory?.length
        ? dataset.expenseByCategory.map((r) =>
            isBusinessExpenseReport(dataset)
              ? [
                  sanitiseBusinessCategoryDisplayName(r.categoryName),
                  String(r.transactionCount ?? 0),
                  `${r.percent.toFixed(1)}%`,
                  formatAud(r.amount),
                ]
              : isFinancialSummary(dataset)
                ? [
                    r.categoryName,
                    String(r.transactionCount ?? 0),
                    `${r.percent.toFixed(1)}%`,
                    formatAud(r.amount),
                  ]
                : [r.categoryName, formatAud(r.amount), `${r.percent.toFixed(1)}%`]
          )
        : isBusinessExpenseReport(dataset) || isFinancialSummary(dataset)
          ? [['No expense data for this period', '—', '—', '—']]
          : [['No expense data for this period', '—', '—']],
      ...(isBusinessExpenseReport(dataset) || isFinancialSummary(dataset)
        ? isBusinessExpenseReport(dataset)
          ? withBusinessPdfFooterMargin(financialSummaryCategoryTableStyles())
          : financialSummaryCategoryTableStyles()
        : {}),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

    if (isBusinessExpenseReport(dataset) && dataset.businessExpenseInsights) {
      y = appendBusinessExpenseVendorTable(doc, dataset, y);
      y = appendBusinessExpenseQuarterlyTable(doc, dataset, y);
      y = appendBusinessExpenseYoyTable(doc, dataset, y);
      y = appendBusinessExpenseInformationalFindings(doc, dataset, y);
    }
  }

  if (dataset.travelMotorVehicle?.length) {
    autoTable(doc, {
      startY: y,
      head: [['Travel & motor vehicle', 'Amount (AUD)', 'Transactions']],
      body: dataset.travelMotorVehicle.map((r) => [
        r.categoryName,
        formatAud(r.amount),
        String(r.transactionCount),
      ]),
      ...(isBusinessExpenseReport(dataset) ? financialSummaryPrimarySectionTableStyles() : {}),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (dataset.reportType === 'monthly_financial_summary' || dataset.budgetPerformance?.length) {
    const budgetHead = isFinancialSummary(dataset)
      ? ['Budget', 'Limit/mo', 'Avg/mo', '% used', 'Status', 'Variance']
      : ['Budget performance', 'Budget', 'Total spent', 'Remaining'];
    autoTable(doc, {
      startY: y,
      head: [budgetHead],
      body: dataset.budgetPerformance?.length
        ? dataset.budgetPerformance.map((r) =>
            isFinancialSummary(dataset)
              ? [
                  r.categoryName,
                  `${formatAud(r.budget)}/mo`,
                  formatAud(r.averageSpentPerMonth),
                  `${r.percentUsed.toFixed(1)}%`,
                  r.status,
                  formatAud(r.periodVariance),
                ]
              : [
                  r.categoryName,
                  formatAud(r.budget),
                  formatAud(r.totalSpent),
                  formatAud(r.remaining),
                ]
          )
        : isFinancialSummary(dataset)
          ? [['No active budgets for this period', '—', '—', '—', '—', '—']]
          : [['No active budgets for this period', '—', '—', '—']],
      ...(isFinancialSummary(dataset)
        ? {
            ...financialSummaryBudgetPerformanceTableStyles(),
            didParseCell: (data) => {
              if (data.section !== 'body' || data.column.index !== 4) return;
              const status = String(data.cell.raw ?? '') as BudgetPerformanceStatus;
              data.cell.styles.textColor = budgetStatusPdfColor(status);
              data.cell.styles.fontStyle = 'bold';
            },
          }
        : {}),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
    const footnotes = dataset.financialSummaryInsights?.footnotes ?? [];
    if (isFinancialSummary(dataset) && footnotes.length) {
      doc.setFontSize(8);
      doc.setTextColor(80);
      for (const note of footnotes) {
        const lines = doc.splitTextToSize(note, 180);
        doc.text(lines, 14, y);
        y += lines.length * 4 + 2;
      }
      doc.setTextColor(0);
      y += 4;
    } else {
      y += 4;
    }
  }

  if (dataset.reportType === 'monthly_financial_summary' || dataset.monthlyTrend?.length) {
    const momHead = isFinancialSummary(dataset)
      ? [
          'Month-over-month',
          'Income',
          'Expenses',
          'Net',
          ...FINANCIAL_SUMMARY_MOM_DELTA_HEADERS,
        ]
      : ['Month-over-Month', 'Income', 'Expenses', 'Net'];
    autoTable(doc, {
      startY: y,
      head: [momHead],
      body: dataset.monthlyTrend?.length
        ? dataset.monthlyTrend.map((r) =>
            isFinancialSummary(dataset)
              ? [
                  r.monthLabel,
                  formatAud(r.income),
                  formatAud(r.expenses),
                  formatAud(r.net),
                  formatChangeAud(r.incomeChange),
                  formatChangeAud(r.expenseChange),
                  formatChangeAud(r.netChange),
                ]
              : [
                  r.monthLabel,
                  formatAud(r.income),
                  formatAud(r.expenses),
                  formatAud(r.net),
                ]
          )
        : isFinancialSummary(dataset)
          ? [['No transaction data for this period', '—', '—', '—', '—', '—', '—']]
          : [['No transaction data for this period', '—', '—', '—']],
      ...(isFinancialSummary(dataset) ? financialSummaryBudgetTableStyles() : {}),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (isFinancialSummary(dataset)) {
    y = appendFinancialSummaryNextSteps(doc, dataset, y);
  }

  if (dataset.deductionThemes?.length) {
    autoTable(doc, {
      startY: y,
      head: [['Deduction Theme', 'Amount', 'Count']],
      body: dataset.deductionThemes.map((t) => [
        t.theme,
        formatAud(t.totalAmount),
        String(t.transactionCount),
      ]),
    });
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  }

  if (dataset.gstSummary) {
    if (isBusinessExpenseReport(dataset)) {
      const gst = dataset.gstSummary;
      const unverified =
        gst.gstUnverifiedSpend ??
        gst.inclusiveTotal + gst.exclusiveTotal;
      autoTable(doc, {
        startY: y,
        head: [['GST — business expenses (informational, not tax advice)', 'Amount (AUD)']],
        body: [
          ['Total business spend', formatAud(unverified)],
          ['GST included (where confirmed)', formatAud(gst.gstIncludedConfirmed ?? 0)],
          ['GST status not verified at transaction level', formatAud(unverified)],
          ['Spend flagged GST-inclusive on category (reference only)', formatAud(gst.inclusiveTotal)],
          ['Spend flagged GST-exclusive on category (reference only)', formatAud(gst.exclusiveTotal)],
        ],
        ...financialSummaryPrimarySectionTableStyles(),
      });
      y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    } else {
      autoTable(doc, {
        startY: y,
        head: [['GST Summary (informational)', 'Amount (AUD)']],
        body: [
          ['GST-inclusive spend', formatAud(dataset.gstSummary.inclusiveTotal)],
          ['GST-exclusive spend', formatAud(dataset.gstSummary.exclusiveTotal)],
          ['Net GST component', formatAud(dataset.gstSummary.netGst)],
        ],
      });
      y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    }
  }

  if (options.includeCharts && dataset.reportType !== 'business_expense') {
    doc.setFontSize(9);
    doc.text(
      'Note: Charts are not included in PDF exports. Use Excel or JSON format for chart data.',
      14,
      y
    );
    y += 8;
  }

  const includeBusinessTxDetail =
    dataset.reportType === 'business_expense' || options.includeRawData;

  if (includeBusinessTxDetail && dataset.rawTransactions?.length) {
    if (isBusinessExpenseReport(dataset)) {
      if (dataset.businessExpenseInsights?.dataNotices.length) {
        y = appendBusinessExpenseDataNotices(doc, dataset, y);
      }
      y = appendBusinessExpenseTransactionsByPrimaryCategory(doc, dataset, y);
    } else {
      const sortedTransactions = [...dataset.rawTransactions].sort((a, b) =>
        a.date.localeCompare(b.date)
      );
      doc.addPage();
      autoTable(doc, {
        startY: 14,
        head: [['#', 'Date', 'Description', 'Category', 'Amount']],
        body: sortedTransactions.map((tx, index) => [
          String(index + 1),
          formatDate(tx.date),
          tx.description,
          tx.categoryName ?? '',
          formatAud(tx.amount),
        ]),
        showFoot: 'everyPage',
      });
    }
  }

  appendPdfFooter(doc, dataset);

  return doc;
}

export function renderReportToPdf(dataset: ReportDataset, options: RenderOptions): Blob {
  const pdfBytes = composeReportPdfDocument(dataset, options).output('arraybuffer') as ArrayBuffer;
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

/** Used by unit tests to assert PDF text content. */
export function renderReport(
  dataset: ReportDataset,
  format: ReportFormat,
  options: RenderOptions
): { blob: Blob; mimeType: string; extension: string } {
  if (format === 'json') {
    const json = renderReportToJson(dataset, options);
    return {
      blob: new Blob([json], { type: 'application/json' }),
      mimeType: 'application/json',
      extension: 'json',
    };
  }
  if (format === 'csv') {
    const csv = renderReportToCsv(dataset, options);
    return {
      blob: new Blob([csv], { type: 'text/csv' }),
      mimeType: 'text/csv',
      extension: 'csv',
    };
  }
  if (format === 'excel') {
    const buffer = renderReportToExcel(dataset, options);
    return {
      blob: new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      extension: 'xlsx',
    };
  }
  const pdfBlob = renderReportToPdf(dataset, options);
  return { blob: pdfBlob, mimeType: 'application/pdf', extension: 'pdf' };
}
